import { formatDistanceToNow } from 'date-fns';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { MarketDataPipeline, MarketDataPipelineStatusDto } from 'shared';
import { toast } from 'sonner';

import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { useRefreshMarketDataPipeline } from '../features/marketHealth/mutations';
import { useMarketDataPipelines } from '../features/marketHealth/queries';

const PIPELINE_COPY: Record<MarketDataPipeline, { title: string; description: string }> = {
  DAILY_BARS: {
    title: 'Daily bars & market regime',
    description:
      'QQQ trend, moving averages, distribution days, FTD/rally, sector rotation and watchlist bars. Also recomputes the HYG/LQD ratio. Takes a few minutes.',
  },
  BREADTH: {
    title: 'Nasdaq-100 breadth',
    description:
      '% above 50DMA, A/D line, new highs and new lows. Takes about 15 minutes to stay within Twelve Data limits.',
  },
  VIX: {
    title: 'VIX',
    description: 'Latest VIX close from FRED.',
  },
  FINANCIAL_CONDITIONS: {
    title: 'Financial conditions',
    description: 'Weekly NFCI and NFCI credit subindex from FRED.',
  },
  PUT_CALL: {
    title: 'Put/call ratio',
    description: 'Cboe equity put/call ratio.',
  },
  CREDIT_RATIO: {
    title: 'HYG/LQD ratio',
    description:
      'Recomputed from stored HYG and LQD bars. Refresh daily bars first if those failed.',
  },
  STOCKBEE_BREADTH: {
    title: 'Stockbee breadth (Market Console · Pulse)',
    description:
      'Stockbee Market Monitor: ±4% movers, 5/10-day ratios, 25%/50%/13% momentum counts, T2108 and S&P 500 close. Checked hourly on weekday evenings.',
  },
  CFTC_POSITIONING: {
    title: 'CFTC positioning (Market Console · Crowding)',
    description:
      'Weekly Commitments of Traders for equity index, metals, energy and crypto futures. The first run backfills about four years.',
  },
  STOCK_LEADERS: {
    title: 'Stock leaders scan (Market Console · Stocks)',
    description:
      'TradingView screener for non-extended and super liquid leaders, plus Yahoo daily bars for the tight-coil check. Runs only outside 09:30–16:15 ET on weekdays.',
  },
  ETF_FLOW: {
    title: 'ETF flow (Market Console · Flow)',
    description:
      'Yahoo daily bars for every theme and sector ETF, plus weekly top holdings with daily liquidity and extension. The first run stores about a year of history.',
  },
  ETF_EXTENDED_HOURS: {
    title: 'ETF extended hours (Market Console · Flow)',
    description:
      'Captures pre-market and after-hours change from TradingView near the end of each session. Manual runs work only during 08:00–09:30 or 16:00–20:30 ET on weekdays.',
  },
};

function relativeTime(value: string | null): string {
  return value ? formatDistanceToNow(new Date(value), { addSuffix: true }) : 'Never';
}

function StatusBadge({ status }: { status: MarketDataPipelineStatusDto }) {
  if (status.run === 'RUNNING') return <Badge tone="neutral">Running</Badge>;
  if (status.run === 'QUEUED') return <Badge tone="neutral">Queued</Badge>;
  if (status.lastAttemptStatus === 'SUCCESS') return <Badge tone="success">OK</Badge>;
  if (status.lastAttemptStatus === 'FAILED') return <Badge tone="danger">Failed</Badge>;
  if (status.lastAttemptStatus === 'NOT_CONFIGURED') {
    return <Badge tone="warning">Not configured</Badge>;
  }
  return <Badge tone="neutral">Never run</Badge>;
}

function PipelineRow({
  status,
  queueAvailable,
}: {
  status: MarketDataPipelineStatusDto;
  queueAvailable: boolean;
}) {
  const refresh = useRefreshMarketDataPipeline();
  const copy = PIPELINE_COPY[status.pipeline];
  const busy = status.run !== 'IDLE' || refresh.isPending;

  return (
    <li className="flex flex-col gap-3 py-4 first:pt-0 last:pb-0 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="font-medium">{copy.title}</h3>
          <StatusBadge status={status} />
        </div>
        <p className="text-sm text-text-muted">{copy.description}</p>
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 text-xs text-text-subtle">
          <dt>Last success</dt>
          <dd>{relativeTime(status.lastSuccessAt)}</dd>
          <dt>Last attempt</dt>
          <dd>{relativeTime(status.lastAttemptAt)}</dd>
        </dl>
        {status.lastAttemptStatus === 'FAILED' && status.lastError ? (
          <p className="break-words text-xs text-danger">{status.lastError}</p>
        ) : null}
        {refresh.isError ? <p className="text-xs text-danger">{refresh.error.message}</p> : null}
      </div>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        className="shrink-0 self-start"
        aria-label={`Refresh ${copy.title}`}
        disabled={!queueAvailable || busy}
        onClick={() =>
          refresh.mutate(status.pipeline, {
            onSuccess: () => toast.success(`${copy.title} refresh queued.`),
          })
        }
      >
        {status.run === 'RUNNING' ? 'Running…' : status.run === 'QUEUED' ? 'Queued' : 'Refresh now'}
      </Button>
    </li>
  );
}

export function MarketDataSettings() {
  const pipelines = useMarketDataPipelines();

  return (
    <main className="min-h-screen bg-bg px-4 py-6 text-text sm:px-6">
      <div className="mx-auto max-w-5xl space-y-6">
        <header className="space-y-2">
          <Link
            to="/settings"
            className="inline-flex items-center gap-1 text-sm text-text-muted hover:text-text"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Settings
          </Link>
          <h1 className="text-2xl font-semibold">Market data</h1>
          <p className="text-sm text-text-muted">
            Market Health pipelines run automatically at 16:30 ET on weekdays. Stockbee breadth is
            checked hourly on weekday evenings, the stock leaders scan at 17:30 ET and ETF flow at
            17:45 ET on weekdays, extended hours at 09:25 and 19:58 ET on weekdays, and CFTC
            positioning on Friday and Monday afternoons. Re-run any that failed.
          </p>
        </header>

        <Card>
          {pipelines.isLoading ? (
            <p className="text-sm text-text-muted">Loading pipelines...</p>
          ) : pipelines.isError || !pipelines.data ? (
            <p className="text-sm text-danger">Could not load market data pipelines.</p>
          ) : (
            <>
              {!pipelines.data.queueAvailable ? (
                <p className="mb-4 text-sm text-warn">
                  Background jobs are not configured on this server, so manual refresh is
                  unavailable.
                </p>
              ) : null}
              <ul className="divide-y divide-border">
                {pipelines.data.pipelines.map((status) => (
                  <PipelineRow
                    key={status.pipeline}
                    status={status}
                    queueAvailable={pipelines.data.queueAvailable}
                  />
                ))}
              </ul>
            </>
          )}
        </Card>
      </div>
    </main>
  );
}
